import React from 'react';
import { Outlet } from 'react-router-dom';

import Footer from '@/components/Footer';
import Header from '@/components/Header';
import { GlobalMarketTicker } from '@/components/market/GlobalMarketTicker';
import { AppTopNav } from '@/components/navbar/AppTopNav';
import { BottomTabBar } from '@/components/navbar/BottomTabBar';
import { useEmbedView } from '@/shared/lib/embed/embed-view';

function MainLayout() {
  // C28 — 앱 «보기» 웹뷰(?embed=1, 문서 3페이지만): 머리글·상단 내비·시세 띠·푸터·하단 탭 없이 본문만.
  const embed = useEmbedView();
  if (embed) {
    return (
      <div className="bg-background text-foreground h-screen flex flex-col">
        <main className="flex-1 overflow-y-auto" data-testid="embed-main">
          <Outlet />
        </main>
      </div>
    );
  }
  return (
    <div className="bg-background text-foreground h-screen flex flex-col">
      {/*
        TRAD-2087 — 종전엔 이 블록이 fixed라 flow 밖으로 빠져서, 아래 본문 행에 실측 px(mt-[85px])로
        수동 보정해야 했다. GlobalMarketTicker가 항목 0건이면 return null로 사라지는데(실제 헤더
        높이가 48px로 줄어듦) mt-[85px]는 그대로라 37px 갭이 뜨는 게 이 티켓의 실제 증상이었다.
        바깥 컨테이너가 이미 h-screen(html/body는 overflow:hidden — window 자체가 안 스크롤됨)이고
        스크롤은 <main>의 overflow-y-auto만 담당하므로, 애초에 fixed일 필요가 없었다. fixed를 빼고
        평범한 flex 자식(shrink-0)으로 두면 아래 행이 flexbox로 자동으로 이어붙어 오프셋 계산 자체가
        필요 없다 — 새 매직넘버로 대체하지 않는다.
      */}
      {/*
        TRAD-3069 — 좌측 사이드바(AppSidebar) → 상단 내비(AppTopNav)로 전환. 사이드바는
        본문 행의 좌측 열이었지만 AppTopNav는 Header 바로 아래를 가로지르는 가로 바라 이
        shrink-0 청크(헤더류) 안으로 옮겨온다. 브레이크포인트는 lg(1024) — md(768)로 두면
        768~799px 구간에서 내비 라벨이 두 줄로 접힌다(실측: docs-ops/2026-09-02-TRAD-3050-
        web-renewal.html §6-3b, 한 줄 유지 최소 폭 800px). BottomTabBar의 lg:hidden과 짝을
        맞춰 768~1023px 태블릿 구간이 내비 없이 비지 않게 한다.
      */}
      {/*
        🔴 TRAD-3074 — davidyoon 이 실사용 중 발견: 상단 내비 드롭다운을 열면 항목 일부가
        본문의 sticky 요소(예: /board/idea "Ideas | Feed" 탭바, MarketDetail의 PriceBar)에
        가려져 클릭이 안 됐다. **2단계 원인**이었다 — 하나만 고치고 끝난 게 아니라 실측으로
        둘 다 확인했다:

        ① 이 wrapper 가 `position: static`이라 `z-10` 클래스가 완전히 무효였다(z-index는
           positioned 요소에서만 스태킹 컨텍스트를 만든다). `relative`를 추가해 실제로
           z-10 스태킹 컨텍스트를 만들게 했다 — **그런데 이것만으론 안 고쳐졌다**(실측:
           고친 뒤에도 elementFromPoint 결과 동일). Playwright로 두 체인(패널/sticky바)의
           조상 전체를 dump해서 원인을 다시 찾았다.

        ② `overflow-y-auto`/`overflow-hidden` 은 스태킹 컨텍스트를 안 만든다(CSS 스펙상
           position+z-index≠auto, sticky/fixed, opacity<1, transform, filter, isolate,
           contain 등만 만든다 — overflow 단독은 트리거가 아니다). 그래서 `<main>` 과
           그 조상들은 전부 스태킹 컨텍스트가 없고, 안에 있는 `sticky` 요소(z-10)는 그
           **루트 스태킹 컨텍스트**에 직접 올라간다. 이 wrapper 도 (①로 고친 뒤) 같은
           루트의 자식이라 **z-10 대 z-10 — 동점**이었고, 동점은 DOM 순서로 갈리는데
           `<main>`(sticky 포함)이 wrapper 보다 뒤에 있어 **항상 sticky 쪽이 이겼다.**
           항목 위치별로 가려짐이 갈린 것도 이 때문 — 패널 전체가 위/아래로 밀린 게
           아니라, sticky 바의 상단 경계선 아래에 걸리는 항목만 진 것이다.

        **고친 값 — z-10 → z-[35].** `src` 전수 grep(2026-09-02) 결과 페이지 내부 sticky
        요소의 z-index 최댓값은 **30**(`PriceBar.tsx` — MarketDetail 상단바), 전체화면
        오버레이(드로어 `EventDetailDrawer`·모바일 메뉴 `BottomTabBar`)는 **40**부터다.
        35는 그 사이 — 페이지 내부 sticky 는 전부 이기고(현재 최대 30, 여유 5), 전체화면
        오버레이가 뜨면 그 아래 깔린다(모달이 내비를 덮는 건 의도된 동작 — 모달 열려 있을
        때 내비 드롭다운이 위로 뚫고 나오면 안 된다).
        🔴 **다음 사람에게**: 페이지 안에 새 `sticky`/`fixed` 요소를 추가할 때 `z-index`를
        **30 넘게 주지 마라** — 넘기면 이 헤더보다 위로 올라가 같은 버그가 재발한다.
        전체화면 모달/드로어급이면 40 이상을 써서 의도적으로 헤더 위에 뜨게 해라.

        재현/검증: `node scripts/verify-trad3074-dropdown-overlap.mjs`(dev 서버 필요,
        jsdom은 레이아웃/페인트를 안 해서 이 버그 자체를 못 잡는다 — vitest 스위트에는
        안 딸려온다).
      */}
      {/* data-chrome — 임베드 정적 HTML 단계에서 CSS 로 숨길 대상(index.html C28 인라인 스크립트). */}
      <div data-chrome className="relative shrink-0 z-[35]">
        <Header />
        <AppTopNav className="hidden lg:flex" />
        <GlobalMarketTicker />
      </div>
      <div className="flex flex-1 overflow-hidden">
        <main className="flex-1 overflow-y-auto pb-14 lg:pb-0 flex flex-col">
          <div className="flex-1">
            <Outlet />
          </div>
          <Footer />
        </main>
      </div>
      <BottomTabBar />
    </div>
  );
}

export default MainLayout;
